import { getMainApi } from '@elva/platform';
import { applyEtagInterceptors } from './etagCache';

let clientReady = false;

function getClient() {
  const api = getMainApi();
  if (!clientReady) {
    applyEtagInterceptors(api);
    clientReady = true;
  }
  return api;
}

/**
 * Per-request transport options. Pass React Query's `signal` from a `queryFn` so the request is
 * aborted when its last observer unmounts (e.g. the page is navigated away from). Mutations never
 * pass one — a write the user started must finish even if they leave the page.
 */
export interface RequestOptions {
  signal?: AbortSignal;
}

function signalConfig(options?: RequestOptions): { signal?: AbortSignal } {
  return options?.signal ? { signal: options.signal } : {};
}

export async function get<T>(
  url: string,
  params?: Record<string, unknown>,
  options?: RequestOptions,
): Promise<T> {
  const res = await getClient().get<T>(url, { params, ...signalConfig(options) });
  return res.data;
}

export async function getBlob(
  url: string,
  params?: Record<string, unknown>,
  options?: RequestOptions,
): Promise<Blob> {
  const res = await getClient().get<Blob>(url, {
    params,
    responseType: 'blob',
    ...signalConfig(options),
  });
  return res.data;
}

export async function post<T>(
  url: string,
  data?: unknown,
  params?: Record<string, unknown>,
  options?: RequestOptions,
): Promise<T> {
  const res = await getClient().post<T>(url, data, { params, ...signalConfig(options) });
  return res.data;
}

export async function patch<T>(
  url: string,
  data?: unknown,
  params?: Record<string, unknown>,
): Promise<T> {
  const res = await getClient().patch<T>(url, data, params ? { params } : undefined);
  return res.data;
}

export async function put<T>(
  url: string,
  data?: unknown,
  params?: Record<string, unknown>,
): Promise<T> {
  const res = await getClient().put<T>(url, data, params ? { params } : undefined);
  return res.data;
}

export async function del<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await getClient().delete<T>(url, { params });
  return res.data;
}

export async function getWithHeaders<T>(
  url: string,
  headers?: Record<string, string>,
  params?: Record<string, unknown>,
  options?: RequestOptions,
): Promise<T> {
  const config: {
    params?: Record<string, unknown>;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  } = {};
  if (params) config.params = params;
  if (headers) config.headers = headers;
  if (options?.signal) config.signal = options.signal;
  const res = await getClient().get<T>(url, config);
  return res.data;
}

export async function postWithHeaders<T>(
  url: string,
  data?: unknown,
  headers?: Record<string, string>,
  params?: Record<string, unknown>,
  options?: RequestOptions,
): Promise<T> {
  const config: {
    params?: Record<string, unknown>;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  } = {};
  if (params) config.params = params;
  if (headers) config.headers = headers;
  if (options?.signal) config.signal = options.signal;
  const res = await getClient().post<T>(url, data, config);
  return res.data;
}

export async function patchWithHeaders<T>(
  url: string,
  data?: unknown,
  headers?: Record<string, string>,
  params?: Record<string, unknown>,
): Promise<T> {
  const config: { params?: Record<string, unknown>; headers?: Record<string, string> } = {};
  if (params) config.params = params;
  if (headers) config.headers = headers;
  const res = await getClient().patch<T>(url, data, config);
  return res.data;
}

export async function postFormData<T>(
  url: string,
  formData: FormData,
  headers?: Record<string, string>,
): Promise<T> {
  const res = await getClient().post<T>(url, formData, {
    headers: headers ? { ...headers, 'Content-Type': 'multipart/form-data' } : { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}
